<template>
  <div>
    <a-form-item label="Profile商品">
      <a-select
        :loading="loadingProfileProductOptions"
        @change="handleProfileProductChange"
        show-search
        :filter-option="filterOption"
        v-decorator="[
          'agents_product_id',
          { rules: [{ required: true, message: '请选择Profile商品' }] },
        ]"
        placeholder="请选择客户已上架Profile"
      >
        <a-select-option
          v-for="record in profileProductOptions"
          :key="record.id"
          :value="record.id"
        >
          {{ record.name }}（{{ record.price }} 元/个）
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="单价(元)">
      <a-input
        :value="unitPriceDisplay"
        disabled
      />
    </a-form-item>

    <a-form-item label="数量" required="required">
      <a-space>
        <a-input-number
          :min="1"
          :max="productCountMaxValue"
          :step="1"
          :precision="0"
          style="width: 120px"
          :formatter="parseProductCountInputValue"
          :parser="parseProductCountInputValue"
          @change="handleProductCountChange"
          v-decorator="['product_count', {
            rules: [
              { required: true, message: '请输入采购数量' }
            ]
          }]"
        />
        <span>个</span>
      </a-space>
    </a-form-item>

    <a-form-item label="产品备注">
      <a-textarea
        v-decorator="['product_remark', {
          rules: [
            { max: 200, message: '最多200个字符' },
          ]
        }]"
        :auto-size="{ minRows: 2, maxRows: 5 }"
      />
    </a-form-item>

    <a-descriptions
      :column="1"
      class="iotplt-descriptions iotplt-descriptions-max-6"
    >
      <a-descriptions-item label="费用(元)">
        {{ productFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="小计(元)">
        {{ totalFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="账户余额(元)">
        {{ balanceDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="可用额度(元)">
        {{ availableDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="已授信(元)">
        {{ creditDisplay }}
      </a-descriptions-item>
    </a-descriptions>

    <a-form-item label="新增授信(元)">
      <a-input-number
        :min="0"
        :max="10000000"
        :step="0.01"
        :precision="2"
        style="width: 100%;"
        v-decorator="['credit', {
          rules: [
            { required: true, message: '请输入授信额度' }
          ]
        }]"
      />
    </a-form-item>
  </div>
</template>

<script>
import { formatFloat } from '@/utils/filter'
import { findAgentsProfileProducts } from '@/api/agents_profile_product'
import { findPreOrderItemLastRemarks } from '@/api/agent_pre_order_item'

export default {
  name: 'NewProfilePreOrderProductForm',
  props: {
    form: {
      type: Object,
      required: true
    },
    agentId: {
      type: Number,
      required: true
    },
    productTypeId: {
      type: Number,
      required: true
    },
    financeOverview: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    this.calculateProductFee = this.$lodash.debounce(this.calculateProductFee, 400)
    return {
      profileProductOptions: [],
      loadingProfileProductOptions: false,
      selectedProfileProduct: null,

      productCountMaxValue: 10000000,
      productFeeDisplay: '',
      totalFeeDisplay: '',
      totalFeeValue: undefined
    }
  },
  computed: {
    balanceDisplay() {
      return this.formatMoney(this.financeOverview.balance)
    },
    availableDisplay() {
      return this.formatMoney(this.financeOverview.available)
    },
    creditDisplay() {
      return this.formatMoney(this.financeOverview.credit)
    },
    unitPriceDisplay() {
      if (!this.selectedProfileProduct) {
        return ''
      }
      return formatFloat(this.selectedProfileProduct.price)
    }
  },
  watch: {
    agentId() {
      this.fetchProfileProductOptions()
    }
  },
  mounted() {
    this.fetchProfileProductOptions()
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined) {
        return '--'
      }
      return formatFloat(value)
    },

    fetchProfileProductOptions() {
      if (!this.agentId) {
        this.profileProductOptions = []
        return
      }
      this.loadingProfileProductOptions = true
      findAgentsProfileProducts({
        agent_id: this.agentId,
        effective: '1',
        page: 1,
        per_page: 1000
      }).then((res) => {
        if (res.code === 0) {
          this.profileProductOptions = res.data || []
        }
        this.loadingProfileProductOptions = false
      })
    },

    handleProfileProductChange(productId) {
      this.selectedProfileProduct = this.profileProductOptions.find((item) => item.id === productId) || null
      this.clearFeeDisplay()
      if (this.selectedProfileProduct) {
        this.calculateProductFee()
        this.fetchPreOrderItemLastRemarks()
      }
    },

    parseProductCountInputValue(val) {
      if (!val) {
        return ''
      }

      if (val < 1) {
        val = 1
      } else if (val > this.productCountMaxValue) {
        val = this.productCountMaxValue
      }
      return parseInt(val)
    },

    handleProductCountChange(productCount) {
      this.clearFeeDisplay()
      if (productCount > 0) {
        this.calculateProductFee()
      }
    },

    calculateProductFee() {
      if (!this.selectedProfileProduct) {
        return
      }
      this.form.validateFields(['product_count'], { force: true }, (err, values) => {
        if (!err) {
          const unitPrice = this.selectedProfileProduct.price
          const totalFee = unitPrice * values.product_count
          this.productFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeValue = totalFee
        }
      })
    },

    clearFeeDisplay() {
      this.productFeeDisplay = ''
      this.totalFeeDisplay = ''
      this.totalFeeValue = undefined
    },

    fetchPreOrderItemLastRemarks() {
      if (!this.selectedProfileProduct) {
        return
      }
      findPreOrderItemLastRemarks({
        agent_id: this.agentId,
        product_type_id: this.productTypeId,
        product_id: this.selectedProfileProduct.id
      }).then((res) => {
        if (res.code === 0) {
          this.form.setFieldsValue({
            product_remark: res.data.product_remark,
            delivery_remark: res.data.delivery_remark,
            invoice_remark: res.data.invoice_remark
          })
        }
      })
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    }
  }
}
</script>
